import type { ComponentProps, ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import { IconButton } from '../../../../components/Button/IconButton';
import type { Signal } from '../../../../domain/signal';
import { LeadPriorityScore } from '../../../../domain/signal';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { LeadPriorityChip } from '../LeadPriorityChip/LeadPriorityChip';
import { LeadScoringAgentSummary } from '../LeadScoringAgentSummary/LeadScoringAgentSummary';

import {
  StyledLeadScoringAgent,
  StyledLeadScoringAgentContent,
  StyledLeadScoringAgentHeader,
  StyledLeadScoringAgentHeaderText,
  StyledLeadScoringButtonWrapper,
} from './styles';

const TITLE_BY_SCORE = {
  [LeadPriorityScore.High]: 'company.signals.lead_scoring_agent.state.high.title',
  [LeadPriorityScore.Medium]: 'company.signals.lead_scoring_agent.state.medium.title',
  [LeadPriorityScore.Low]: 'company.signals.lead_scoring_agent.state.low.title',
  [LeadPriorityScore.Unlock]: 'company.signals.lead_scoring_agent.state.unlock.title',
  [LeadPriorityScore.NoScore]: 'company.signals.lead_scoring_agent.state.no_score.title',
};

interface ILeadScoringAgentProps extends ComponentProps<typeof LeadPriorityChip> {
  domain: string;
  signals: readonly Signal[];
}

export const LeadScoringAgent = ({ score, domain, signals }: ILeadScoringAgentProps): ReactElement => {
  const { t } = useTranslation();

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleAllSignalsClick = () => {
    dispatchTrackEventWithLocation('signals', 'open signals in platform', 'see all signals');
    window.open(
      `https://pro.similarweb.com/#/sales/account-overview/${domain}/signals?isShowManageSignals=true`,
      '_blank'
    );
  };

  const handleScoreSettingsClick = () => {
    dispatchTrackEventWithLocation('signals', 'open score settings', 'score settings');
    window.open('https://pro.similarweb.com/#/sales/score-settings', '_blank');
  };

  return (
    <StyledLeadScoringAgent>
      <StyledLeadScoringAgentHeader>
        <StyledLeadScoringAgentHeaderText>{t(TITLE_BY_SCORE[score])}</StyledLeadScoringAgentHeaderText>
        <LeadPriorityChip score={score} />
      </StyledLeadScoringAgentHeader>

      <StyledLeadScoringAgentContent>
        <LeadScoringAgentSummary domain={domain} score={score} triggeredSignals={signals} />

        {score === LeadPriorityScore.Unlock ? (
          <StyledLeadScoringButtonWrapper>
            <IconButton iconName="settings" onClick={handleAllSignalsClick}>
              {t('company.signals.lead_scoring_agent.state.unlock.button')}
            </IconButton>
          </StyledLeadScoringButtonWrapper>
        ) : null}

        {score === LeadPriorityScore.NoScore ? (
          <StyledLeadScoringButtonWrapper>
            <IconButton iconName="settings" onClick={handleScoreSettingsClick}>
              {t('company.signals.lead_scoring_agent.state.no_score.button')}
            </IconButton>
          </StyledLeadScoringButtonWrapper>
        ) : null}
      </StyledLeadScoringAgentContent>
    </StyledLeadScoringAgent>
  );
};
